import CommonJs from '@/utils/commonJs';

export { appendStaticFileAuthParams } from '@/utils/staticFileAuth';

const OFFICE_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp'];
const PDF_EXTENSIONS = ['.pdf'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.flac', '.aac', '.m4a'];

const QUERY_NAME_KEYS = ['path', 'file', 'filepath', 'filePath', 'filename', 'name'];
/** Prefix lưu trữ BE: `{uuid}_ten-file.ext` hoặc `{digits}_ten-file.ext`. */
const STORED_FILE_PREFIX_RE =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)_/i;

function safeDecodeUri(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Bỏ prefix UUID/số mà BE gắn khi lưu file, để UI hiện tên gốc. */
export function stripStoredFileNamePrefix(fileName: string): string {
  const raw = String(fileName || '').trim();
  if (!raw) return '';
  const stripped = raw.replace(STORED_FILE_PREFIX_RE, '');
  return stripped || raw;
}

/** Decode tên file từ path/URL (pathname browser vẫn còn %C3%94…). */
export function decodeFileNameSegment(segment: string): string {
  let out = String(segment || '').trim();
  if (!out) return '';
  for (let i = 0; i < 2; i += 1) {
    try {
      const next = decodeURIComponent(out.replace(/\+/g, ' '));
      if (next === out) break;
      out = next;
    } catch {
      break;
    }
  }
  return out;
}

function stripAmpQuery(segment: string): string {
  const amp = segment.indexOf('&');
  return (amp >= 0 ? segment.slice(0, amp) : segment).trim();
}

function lastPathSegment(pathLike: string): string {
  const cleaned = pathLike.replace(/\\/g, '/').replace(/\/+$/, '');
  const parts = cleaned.split('/').filter(Boolean);
  if (!parts.length) return stripAmpQuery(cleaned);
  return stripAmpQuery(parts[parts.length - 1]);
}

export function truncateMiddlePreserveExt(filename: string, max = 28): string {
  if (filename.length <= max) return filename;
  const lastDot = filename.lastIndexOf('.');
  const namePart = lastDot > 0 ? filename.slice(0, lastDot) : filename;
  const extPart = lastDot > 0 ? filename.slice(lastDot) : '';
  const budget = max - extPart.length - 3;
  if (budget <= 0) return filename;
  const head = Math.ceil(budget * 0.6);
  const tail = budget - head;
  return `${namePart.slice(0, head)}...${namePart.slice(-tail)}${extPart}`;
}

/** Link: full URL (cắt đuôi bằng CSS `text-overflow: ellipsis` trên UI). */
export function formatUrlDisplayLabel(filePath: string): string {
  return safeDecodeUri(String(filePath || '').trim());
}

/** Nhãn hiển thị: link → full URL; file → `truncateMiddlePreserveExt` như cũ. */
export function formatAttachmentDisplayLabel(filePath: string): string {
  const decoded = safeDecodeUri(String(filePath || '').trim());
  if (/^https?:\/\//i.test(decoded) && isExternalLinkAttachment(decoded)) {
    return formatUrlDisplayLabel(decoded);
  }
  return truncateMiddlePreserveExt(extractFileNameFromUrl(decoded));
}

export function extractFileNameFromUrl(filePath: string): string {
  const decoded = safeDecodeUri(String(filePath || '').trim());
  if (!decoded) return '';

  let rawName = '';

  if (/^https?:\/\//i.test(decoded)) {
    try {
      const url = new URL(decoded);

      for (const key of QUERY_NAME_KEYS) {
        const param = url.searchParams.get(key);
        if (param) {
          const segment = lastPathSegment(param);
          if (segment) {
            rawName = segment;
            break;
          }
        }
      }

      if (!rawName) {
        const fromPathname = lastPathSegment(url.pathname);
        if (fromPathname) rawName = fromPathname;
      }

      if (!rawName) rawName = url.hostname.replace(/^www\./i, '');
    } catch {
      // fall through
    }
  }

  if (!rawName) {
    const withoutHash = decoded.split('#')[0];
    const withoutQuery = withoutHash.split('?')[0];
    const segment = lastPathSegment(withoutQuery);
    rawName = segment || stripAmpQuery(decoded);
  }

  return stripStoredFileNamePrefix(decodeFileNameSegment(rawName));
}

function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : '';
}

export function isPreviewableFileName(fileName: string): boolean {
  const ext = getFileExtension(fileName);
  if (OFFICE_EXTENSIONS.includes(ext)) return true;
  if (PDF_EXTENSIONS.includes(ext)) return true;
  if (VIDEO_EXTENSIONS.includes(ext)) return true;
  if (AUDIO_EXTENSIONS.includes(ext)) return true;
  return CommonJs.isImageFile(fileName);
}

/** Link chèn URL (không phải file đính kèm) — ẩn nút tải xuống. */
export function isExternalLinkAttachment(fileUrl: string, fileName?: string): boolean {
  const url = String(fileUrl || '').trim();
  if (!url) return false;
  if (url.includes('/file/static/')) return false;

  const name = (fileName || extractFileNameFromUrl(url)).trim();
  return !isPreviewableFileName(name);
}
